import React, { useState, useEffect } from "react";
import "./PathologyRecord.css";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "./../../../../../AppProvider";
import { useAuth } from "./../../../../../AuthProvider";
import api from "./../../../../../axiosInstance";

const EMPTY_MED = { brand: '', drugsName: '', quantity: '', duration: '', dosage: '', remarks: '' };
const EMPTY_TEST = { test: '', department: '' };

const PrescriptionRecord = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { patientsMHR, setPatientsMHR, selectedClinic, prescriptionOptions, selectedPatientObj } = useApp();

  const [fileUrls, setFileUrls] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedMeds, setEditedMeds] = useState([]);
  const [editedTests, setEditedTests] = useState([]);
  const [editedSymptoms, setEditedSymptoms] = useState('');
  const [editedDiagnosis, setEditedDiagnosis] = useState('');
  const [editedAdvice, setEditedAdvice] = useState('');
  const [showResendPopup, setShowResendPopup] = useState(false);
  const [resendLang, setResendLang] = useState('en');
  const [resendStatus, setResendStatus] = useState(null); // null | 'sending' | 'success' | 'error'
  const [resendError, setResendError] = useState('');

  let { recordId } = useParams();
  if (recordId === undefined) {
    recordId = patientsMHR[0].recordId;
    navigate(`/DashboardDashboardNavigation/records/${recordId}`);
  }

  const record = patientsMHR.find((item) => item.recordId === recordId);

  const isOwner =
    record.doctorId?.toString() === user?._id?.toString() &&
    record.clinicId?.toString() === selectedClinic?._id?.toString();

  useEffect(() => {
    const fetchPdfs = async () => {
      const urls = await Promise.all(
        record.files.map(async (fileId) => {
          const response = await api.get(`/uploads/${fileId}`, {
            responseType: 'blob'
          });
          return URL.createObjectURL(response.data);
        })
      );
      setFileUrls(urls);
    };
    if (record.files && record.files.length > 0) fetchPdfs();
  }, [record]);

  const handleEditStart = () => {
    setEditedMeds(record.medications.map((m) => ({ ...m })));
    setEditedTests((record.test || []).map((t) => ({ ...t })));
    setEditedSymptoms(record.symptoms || '');
    setEditedDiagnosis(record.diagnosis || '');
    setEditedAdvice(record.advice || '');
    setIsEditing(true);
  };

  const handleMedChange = (index, field, value) => {
    setEditedMeds((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [field]: value } : m))
    );
  };

  const handleMedDelete = (index) => {
    setEditedMeds((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddMedRow = () => {
    setEditedMeds((prev) => [...prev, { ...EMPTY_MED }]);
  };

  const handleTestChange = (index, field, value) => {
    setEditedTests((prev) =>
      prev.map((t, i) => (i === index ? { ...t, [field]: value } : t))
    );
  };

  const handleTestDelete = (index) => {
    setEditedTests((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddTestRow = () => {
    setEditedTests((prev) => [...prev, { ...EMPTY_TEST }]);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { data } = await api.put(`/mhr/${record._id}`, {
        medications: editedMeds,
        test: editedTests,
        symptoms: editedSymptoms,
        diagnosis: editedDiagnosis,
        advice: editedAdvice,
      });
      setPatientsMHR((prev) =>
        prev.map((r) => (r.recordId === recordId ? { ...r, ...data } : r))
      );
      setIsEditing(false);
    } catch (err) {
      alert(`Error saving: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResend = async () => {
    setResendStatus('sending');
    setResendError('');
    try {
      await api.post(`/mhr/${record._id}/send-pdf`, null, { params: { locale: resendLang } });
      setResendStatus('success');
    } catch (err) {
      setResendError(err.response?.data?.message || err.message || 'Failed to send PDF.');
      setResendStatus('error');
    }
  };

  const handleResendClose = () => {
    setShowResendPopup(false);
    setResendStatus(null);
    setResendError('');
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/mhr/${record._id}`);
      const remaining = patientsMHR.filter((r) => r.recordId !== recordId);
      setPatientsMHR(remaining);
      if (remaining.length > 0) {
        navigate(`/DashboardDashboardNavigation/records/${remaining[0].recordId}`);
      } else {
        navigate('/DashboardDashboardNavigation/records');
      }
    } catch (err) {
      alert(`Error deleting: ${err.message}`);
    } finally {
      setConfirmDelete(false);
    }
  };

  const meds = isEditing ? editedMeds : record.medications;
  const tests = isEditing ? editedTests : (record.test || []);

  return (
    <div className="card-container-main rx-prescription-page">

      {/* Header row */}
      <div className="prescription-record-header">
        <h2 className="card-heading-1">Prescription Record</h2>
        {isOwner && !isEditing && (
          <div className="rx-record-actions">
            <button className="rx-record-btn rx-resend-open-btn" onClick={() => setShowResendPopup(true)}>Resend Prescription </button>
            <button className="rx-record-btn rx-record-edit-btn" onClick={handleEditStart}>Edit</button>
            <button className="rx-record-btn rx-record-delete-btn" onClick={() => setConfirmDelete(true)}>Delete</button>
          </div>
        )}
        {isEditing && (
          <div className="rx-record-actions">
            <button className="rx-record-btn rx-record-cancel-btn" onClick={() => setIsEditing(false)}>Cancel</button>
            <button className="rx-record-btn rx-record-save-btn" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {/* Prescription meta info */}
      <div className="rx-meta-card">
        <div className="rx-meta-row">
          <div className="rx-meta-item">
            <span className="rx-meta-label">Doctor</span>
            <span className="rx-meta-value">{record.doctorName || '—'}{record.qualification ? ` · ${record.qualification}` : ''}</span>
          </div>
          {record.registrationNumber && (
            <div className="rx-meta-item">
              <span className="rx-meta-label">Reg. No.</span>
              <span className="rx-meta-value">{record.registrationNumber}</span>
            </div>
          )}
          <div className="rx-meta-item">
            <span className="rx-meta-label">Date</span>
            <span className="rx-meta-value">
              {record.dateOfPublish ? new Date(record.dateOfPublish).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
            </span>
          </div>
        </div>
        <div className="rx-meta-row">
          {record.clinicAdd && (
            <div className="rx-meta-item">
              <span className="rx-meta-label">Clinic</span>
              <span className="rx-meta-value">{record.clinicAdd}</span>
            </div>
          )}
          {record.phoneNumber && (
            <div className="rx-meta-item">
              <span className="rx-meta-label">Phone</span>
              <span className="rx-meta-value">{record.phoneNumber}</span>
            </div>
          )}
          {record.appointmentId && (
            <div className="rx-meta-item">
              <span className="rx-meta-label">Appointment ID</span>
              <span className="rx-meta-value rx-meta-appt-id">{record.appointmentId}</span>
            </div>
          )}
        </div>
      </div>

      {/* Symptoms / Diagnosis */}
      <div className="prescription-record-columns">
        <div className="cards-section-symtoms">
          <div className="cards-container-tag">
            <h2>Symptoms</h2>
            <hr className="horizontal-line" />
            {isEditing ? (
              <textarea className="rx-edit-textarea" value={editedSymptoms} onChange={(e) => setEditedSymptoms(e.target.value)} />
            ) : (
              <p className="prescription-para">{record.symptoms || ''}</p>
            )}
          </div>
        </div>
        <div className="cards-section-dignosis">
          <div className="cards-container-tag">
            <h2>Diagnosis</h2>
            <hr className="horizontal-line" />
            {isEditing ? (
              <textarea className="rx-edit-textarea" value={editedDiagnosis} onChange={(e) => setEditedDiagnosis(e.target.value)} />
            ) : (
              <p className="prescription-para">{record.diagnosis || ''}</p>
            )}
          </div>
        </div>
      </div>

      {/* Advice / Remember */}
      <div className="prescription-record-columns">
        <div className="cards-section-advice">
          <div className="cards-container-tag">
            <h2>Advice</h2>
            <hr className="horizontal-line" />
            {isEditing ? (
              <textarea className="rx-edit-textarea" value={editedAdvice} onChange={(e) => setEditedAdvice(e.target.value)} />
            ) : (
              <p className="prescription-para">{record.advice || ''}</p>
            )}
          </div>
        </div>
        <div className="cards-section-remember">
          <div className="cards-container-tag">
            <h2>Things to remember</h2>
            <hr className="horizontal-line" />
            <p className="prescription-para">{record.remember || ''}</p>
          </div>
        </div>
      </div>

      {/* Medicines */}
      <h2 className="prescription-heading-medicine">Medicines</h2>
      <table className="prescription__medi-table">
        <thead>
          <tr className="prescription__medi-table-header">
            <th className="prescription__medi-header-text">S.No.</th>
            <th className="prescription__medi-header-text">Brand Name</th>
            <th className="prescription__medi-header-text">Drug Name</th>
            <th className="prescription__medi-header-text">Quantity</th>
            <th className="prescription__medi-header-text">Duration</th>
            <th className="prescription__medi-header-text">Dosage</th>
            <th className="prescription__medi-header-text">Remarks</th>
            {isEditing && <th className="prescription__medi-header-text rx-col-action"></th>}
          </tr>
        </thead>
        <tbody>
          {meds.length > 0 ? (
            meds.map((group, index) => (
              <tr className="prescription__medi-table-body" key={index}>
                <td className="prescription__medi-table-el serialNo">{index + 1}</td>
                <td className="prescription__medi-table-el">
                  {isEditing ? (
                    <input className="rx-edit-input" value={group.brand || ''} onChange={(e) => handleMedChange(index, 'brand', e.target.value)} placeholder="Brand name" />
                  ) : group.brand}
                </td>
                <td className="prescription__medi-table-el">
                  {isEditing ? (
                    <input className="rx-edit-input" value={group.drugsName || ''} onChange={(e) => handleMedChange(index, 'drugsName', e.target.value)} placeholder="Drug name" />
                  ) : group.drugsName}
                </td>
                <td className="prescription__medi-table-el">
                  {isEditing ? (
                    <select className="rx-edit-select" value={group.quantity || ''} onChange={(e) => handleMedChange(index, 'quantity', e.target.value)}>
                      <option value="">Select</option>
                      {(prescriptionOptions?.quantity || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : group.quantity}
                </td>
                <td className="prescription__medi-table-el">
                  {isEditing ? (
                    <select className="rx-edit-select" value={group.duration || ''} onChange={(e) => handleMedChange(index, 'duration', e.target.value)}>
                      <option value="">Select</option>
                      {(prescriptionOptions?.duration || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : group.duration}
                </td>
                <td className="prescription__medi-table-el">
                  {isEditing ? (
                    <select className="rx-edit-select" value={group.dosage || ''} onChange={(e) => handleMedChange(index, 'dosage', e.target.value)}>
                      <option value="">Select</option>
                      {(prescriptionOptions?.dosage || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : group.dosage}
                </td>
                <td className="prescription__medi-table-el">
                  {isEditing ? (
                    <input className="rx-edit-input" value={group.remarks || ''} onChange={(e) => handleMedChange(index, 'remarks', e.target.value)} placeholder="Remarks" />
                  ) : group.remarks}
                </td>
                {isEditing && (
                  <td className="prescription__medi-table-el rx-col-action">
                    <button className="rx-row-delete-btn" onClick={() => handleMedDelete(index)} title="Remove row">✕</button>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={isEditing ? 8 : 7} className="medi-no-medicine-selected">Medicine Not Alloted</td>
            </tr>
          )}
        </tbody>
      </table>
      {isEditing && (
        <button className="rx-add-row-btn" onClick={handleAddMedRow}>+ Add Medicine</button>
      )}

      {/* Tests */}
      <h2 className="Prescription__cards-heading-test">Tests</h2>
      <table className="prescription__medi-table">
        <thead>
          <tr className="prescription__medi-table-header">
            <th className="prescription__medi-header-text serialNo">S.No.</th>
            <th className="prescription__medi-header-text">Lab Name</th>
            <th className="prescription__medi-header-text">Test</th>
            {isEditing && <th className="prescription__medi-header-text rx-col-action"></th>}
          </tr>
        </thead>
        <tbody>
          {tests.length > 0 ? (
            tests.map((item, index) => (
              <tr className="prescription__medi-table-body" key={index}>
                <td className="prescription__medi-table-el serialNo">{index + 1}</td>
                <td className="prescription__medi-table-el">
                  {isEditing ? (
                    <input className="rx-edit-input" value={item.test || ''} onChange={(e) => handleTestChange(index, 'test', e.target.value)} placeholder="Lab name" />
                  ) : item.test}
                </td>
                <td className="prescription__medi-table-el">
                  {isEditing ? (
                    <input className="rx-edit-input" value={item.department || ''} onChange={(e) => handleTestChange(index, 'department', e.target.value)} placeholder="Test name" />
                  ) : item.department}
                </td>
                {isEditing && (
                  <td className="prescription__medi-table-el rx-col-action">
                    <button className="rx-row-delete-btn" onClick={() => handleTestDelete(index)} title="Remove row">✕</button>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={isEditing ? 4 : 3} className="medi-no-medicine-selected">Test Not Alloted</td>
            </tr>
          )}
        </tbody>
      </table>
      {isEditing && (
        <button className="rx-add-row-btn" onClick={handleAddTestRow}>+ Add Test</button>
      )}

      {/* Resend WhatsApp popup */}
      {showResendPopup && (
        <div className="rx-popup-overlay">
          <div className="rx-resend-popup">
            <h3 className="rx-resend-title">Resend Prescription PDF</h3>

            {resendStatus !== 'success' && (
              <p className="rx-resend-msg">
                A prescription PDF will be sent to{' '}
                <strong>
                  {[selectedPatientObj?.name?.firstName, selectedPatientObj?.name?.lastName].filter(Boolean).join(' ')}
                </strong>{' '}
                WhatsApp ({selectedPatientObj?.contact?.phoneNumber?.phoneNumber || '—'}).
              </p>
            )}

            {resendStatus === 'sending' && (
              <div className="rx-resend-status rx-resend-status--sending">
                Sending PDF, please wait…
              </div>
            )}

            {resendStatus === 'success' && (
              <div className="rx-resend-status rx-resend-status--success">
                PDF sent successfully to WhatsApp!
              </div>
            )}

            {resendStatus === 'error' && (
              <div className="rx-resend-status rx-resend-status--error">
                {resendError}
              </div>
            )}

            {resendStatus !== 'success' && (
              <div className="rx-resend-lang-row">
                <label className="rx-resend-lang-label">Language</label>
                <select className="rx-resend-lang-select" value={resendLang} onChange={(e) => setResendLang(e.target.value)} disabled={resendStatus === 'sending'}>
                  <option value="en">English</option>
                  <option value="hi">Hindi</option>
                  <option value="mr">Marathi</option>
                  <option value="gu">Gujarati</option>
                </select>
              </div>
            )}

            <div className="rx-resend-actions">
              {resendStatus === 'success' ? (
                <button className="rx-record-btn rx-record-cancel-btn" onClick={handleResendClose}>Close</button>
              ) : (
                <>
                  <button className="rx-record-btn rx-record-cancel-btn" onClick={handleResendClose} disabled={resendStatus === 'sending'}>Cancel</button>
                  <button className="rx-record-btn rx-resend-send-btn" onClick={handleResend} disabled={resendStatus === 'sending'}>
                    {resendStatus === 'sending' ? 'Sending…' : resendStatus === 'error' ? 'Retry' : 'Send'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation popup */}
      {confirmDelete && (
        <div className="rx-popup-overlay">
          <div className="rx-delete-confirm-popup">
            <h3 className="rx-delete-confirm-title">Delete Prescription?</h3>
            <p className="rx-delete-confirm-msg">This prescription will be permanently deleted and cannot be undone.</p>
            <div className="rx-delete-confirm-actions">
              <button className="rx-record-btn rx-record-cancel-btn" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="rx-record-btn rx-record-delete-btn" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrescriptionRecord;
